/**
 * The recipes the planner picks a week from.
 *
 * The planner, onboarding, « Planifier les 7 jours suivants » and the older
 * desktop layout's sheets used to read `personalRecipeIndex` alone — the 481
 * recipes bundled in the app — while Régénérer, « Remplacer » and the Recipes
 * screen read the shared catalogue (1420 recipes) plus the cook's own saved
 * ones. So a new cook's first week came from a third of the catalogue, and a
 * recipe the cook imported could never be planned for them.
 *
 * Now every generator starts from the same list `unifiedRecipeService` gives
 * the rest of the app: shared catalogue + the cook's saved recipes − the ones
 * they hid. The bundled index is only the fallback, for when that list is not
 * there: Firebase off, offline, or slower than onboarding can wait.
 *
 * Both halves pass the reader's language gate (#737): a shared recipe that is
 * not readable in the cook's language is never planned for them, and a cook's
 * own saved recipes are never hidden (`isOfferedTo`).
 */
import personalRecipeIndex, { mapEntriesToPersonal, type PersonalRecipeIndexEntry } from './personalRecipeIndex';
import personalRecipeDetails from './personalRecipeDetails';
import unifiedRecipeService, { type RecipeIndexEntry } from './unifiedRecipeService';
import { isOfferedTo } from '../utils/recipeLanguage';
import type { Recipe } from '../types/recipe';

/**
 * How long onboarding waits for the catalogue before planning from the
 * bundled recipes. The catalogue load keeps going in the background, and its
 * cache serves the next generation.
 */
export const ONBOARDING_LIBRARY_WAIT_MS = 4000;

/**
 * The planner's library from the unified list and the bundled index. The
 * unified list wins whenever it holds the shared catalogue: it already carries
 * every bundled recipe (#496 backfill), and adding the bundled copies back
 * would resurrect the recipes the cook hid. When it came back without the
 * catalogue — the shared read failed, and only the saved recipes arrived —
 * the bundled recipes fill in behind the cook's own. Pure; exported for tests.
 */
export function mergePlannerLibrary(
  unified: RecipeIndexEntry[],
  bundled: PersonalRecipeIndexEntry[],
): PersonalRecipeIndexEntry[] {
  if (unified.length === 0) return bundled;
  const mapped = mapEntriesToPersonal(unified);
  const shared = unified.filter((e) => e.source === 'base').length;
  if (shared >= bundled.length / 2) return mapped;
  const have = new Set(mapped.map((e) => e.id));
  return [...mapped, ...bundled.filter((e) => !have.has(e.id))];
}

function withDeadline<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      () => { clearTimeout(timer); resolve(fallback); },
    );
  });
}

export interface PlannerLibraryOptions {
  /** The reader's language: shared recipes they cannot read are left out. */
  language: 'en' | 'fr';
  /** The signed-in cook, whose saved and hidden recipes count. */
  uid?: string | null;
  /** The recipe index already in the store, when a screen has loaded it. */
  indexed?: RecipeIndexEntry[];
  /** Give up on the catalogue after this long and plan from the bundle. */
  deadlineMs?: number;
}

export async function loadPlannerLibrary(opts: PlannerLibraryOptions): Promise<PersonalRecipeIndexEntry[]> {
  const { language } = opts;
  const bundled = personalRecipeIndex.getAllRecipes(language);
  let unified = opts.indexed ?? [];
  if (unified.length === 0) {
    const load = unifiedRecipeService.getAllRecipes(opts.uid ?? undefined);
    unified = opts.deadlineMs ? await withDeadline(load, opts.deadlineMs, []) : await load.catch(() => []);
  }
  return mergePlannerLibrary(unified.filter((e) => isOfferedTo(e, language)), await bundled);
}

/**
 * A planned recipe's full details: the bundled file for a bundled recipe,
 * Firestore for everything else (`getRecipeDetails` never asks the bundle for
 * a recipe it does not hold).
 */
export async function plannerRecipeDetails(id: string, uid?: string | null): Promise<Recipe | null> {
  return (await personalRecipeDetails.getRecipeDetails(id))
    ?? unifiedRecipeService.getRecipeById(id, uid ?? undefined);
}
